/**
 * Quiz Results Page: AJAX Cart & Analytics
 * Implements instant add to cart, cart drawer, and comprehensive analytics tracking
 */

(function() {
  'use strict';

  // Product handle mapping (category + bundle type → product handle)
  // Reconciled against live Shopify catalog 2026-04-07
  const PRODUCT_HANDLES = {
    'mind-calm': {
      'starter-set': 'mind-calm-stater-set',
      'booster-set': 'mind-calm-booster-pack-cycle-support',
      'full-set': 'mind-calm-ritual-set'
    },
    'deep-restore': {
      'starter-set': 'deep-restore-starter-set',
      'booster-set': 'deep-restore-booster-set-body-repair',
      'full-set': 'deep-restore-ritual-set'
    },
    'sacred-soothe': {
      'starter-set': 'sacred-soothe-starter-pack',
      'booster-set': 'sacred-soothe-booster-set',
      'full-set': 'sacred-soothe-ritual-set-1'
    },
    'circadian-reset': {
      'starter-set': 'circadian-reset-starter-set',
      'booster-set': 'circadian-reset-booster-set-holistic-support',
      'full-set': 'circadian-reset-ritual-set'
    }
  };

  /**
   * Get product handle from category and bundle type
   */
  function getProductHandle(category, bundleType) {
    const bundleKey = bundleType.toLowerCase().replace(/\s+/g, '-');
    return PRODUCT_HANDLES[category]?.[bundleKey] || null;
  }

  /**
   * Get product variant ID from handle
   * Falls back to fetching from Shopify if not cached
   */
  async function getProductVariantId(handle) {
    try {
      // Try to get from cache first
      const cacheKey = `veluna_variant_${handle}`;
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        return JSON.parse(cached);
      }

      // Fetch product data from Shopify
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        throw new Error('Product not found');
      }

      const product = await response.json();
      const variantId = product.variants && product.variants[0] ? product.variants[0].id : null;

      // Cache for session
      if (variantId) {
        sessionStorage.setItem(cacheKey, JSON.stringify(variantId));
      }

      return variantId;
    } catch (error) {
      console.error('Error fetching product variant:', error);
      return null;
    }
  }

  /**
   * Add product to cart via AJAX
   */
  async function addToCart(variantId, quantity = 1, metadata = {}) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity,
          properties: metadata
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Failed to add to cart');
      }

      const cartItem = await response.json();
      return cartItem;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  /**
   * Update cart count in header
   */
  async function updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count, .js-cart-count');
      cartCountElements.forEach(el => {
        el.textContent = cart.item_count || 0;
        el.style.display = cart.item_count > 0 ? '' : 'none';
      });

      return cart;
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  /**
   * Show cart drawer/sidebar
   */
  function showCartDrawer() {
    // Trigger Shopify cart drawer if available
    if (typeof window.theme !== 'undefined' && window.theme.cartDrawer) {
      window.theme.cartDrawer.open();
    } else {
      // Fallback: redirect to cart page
      window.location.href = '/cart';
    }
  }

  /**
   * Show success notification
   */
  function showCartNotification(message, duration = 3000) {
    // Remove existing notification
    const existing = document.querySelector('.veluna-cart-notification');
    if (existing) {
      existing.remove();
    }

    // Create notification
    const notification = document.createElement('div');
    notification.className = 'veluna-cart-notification';
    notification.innerHTML = `
      <div class="veluna-cart-notification__content">
        <svg class="veluna-cart-notification__icon" viewBox="0 0 20 20" fill="none" stroke="currentColor">
          <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
        </svg>
        <span class="veluna-cart-notification__message">${message}</span>
      </div>
    `;

    document.body.appendChild(notification);

    // Animate in
    setTimeout(() => {
      notification.classList.add('veluna-cart-notification--visible');
    }, 10);

    // Remove after duration
    setTimeout(() => {
      notification.classList.remove('veluna-cart-notification--visible');
      setTimeout(() => notification.remove(), 300);
    }, duration);
  }

  /**
   * Track analytics events
   */
  function trackEvent(eventName, eventData = {}) {
    // Google Analytics 4
    try {
      if (typeof gtag !== 'undefined') {
        gtag('event', eventName, {
          event_category: 'Quiz Results',
          ...eventData
        });
      }
    } catch (error) {
      console.warn('Quiz Results Cart: GA4 tracking error:', error);
    }

    // Facebook Pixel (with error handling for ad blockers)
    try {
      if (typeof fbq !== 'undefined') {
        const metaContext = window.VelunaAnalytics && window.VelunaAnalytics.getMetaContext
          ? window.VelunaAnalytics.getMetaContext({ category: eventData.category, bundle: eventData.bundle })
          : {
              quizCategory: eventData.category || '',
              quizBundle: eventData.bundle || '',
              metaSegment: (eventData.category || '') + '_' + (eventData.bundle || ''),
              behaviorStage: '',
              audienceTemperature: '',
              audienceStage: '',
              adforgeSegment: ''
            };

        if (eventName === 'add_to_cart') {
          const contentIds = eventData.content_ids || (eventData.product_handle ? [eventData.product_handle] : []);
          fbq('track', 'AddToCart', {
            content_type: 'product',
            content_ids: contentIds,
            content_name: eventData.product_name || '',
            content_category: metaContext.quizCategory || eventData.category || '',
            value: eventData.value,
            currency: 'USD',
            quiz_category: metaContext.quizCategory,
            quiz_bundle: metaContext.quizBundle,
            meta_segment: metaContext.metaSegment,
            behavior_stage: 'quiz_results_add_to_cart',
            audience_temperature: metaContext.audienceTemperature,
            audience_stage: metaContext.audienceStage,
            adforge_segment: metaContext.adforgeSegment,
            recommendation_source: 'quiz',
            product_handle: eventData.product_handle || ''
          });
        } else if (eventName === 'view_item') {
          const contentIds = eventData.content_ids || (eventData.product_handle ? [eventData.product_handle] : []);
          fbq('track', 'ViewContent', {
            content_type: 'product',
            content_ids: contentIds,
            content_name: eventData.product_name || '',
            content_category: metaContext.quizCategory || eventData.category || '',
            value: eventData.value,
            currency: 'USD',
            quiz_category: metaContext.quizCategory,
            quiz_bundle: metaContext.quizBundle,
            meta_segment: metaContext.metaSegment,
            behavior_stage: 'quiz_results_view',
            audience_temperature: metaContext.audienceTemperature,
            audience_stage: metaContext.audienceStage,
            adforge_segment: metaContext.adforgeSegment,
            recommendation_source: 'quiz',
            product_handle: eventData.product_handle || ''
          });
        } else if (eventName === 'begin_checkout') {
          const contentIds = eventData.content_ids || (eventData.product_handle ? [eventData.product_handle] : []);
          fbq('track', 'InitiateCheckout', {
            content_type: 'product',
            content_ids: contentIds,
            content_name: eventData.product_name || '',
            content_category: metaContext.quizCategory || eventData.category || '',
            value: eventData.value,
            currency: 'USD',
            quiz_category: metaContext.quizCategory,
            quiz_bundle: metaContext.quizBundle,
            meta_segment: metaContext.metaSegment,
            behavior_stage: 'quiz_results_begin_checkout',
            audience_temperature: metaContext.audienceTemperature,
            audience_stage: metaContext.audienceStage,
            adforge_segment: metaContext.adforgeSegment,
            recommendation_source: 'quiz',
            product_handle: eventData.product_handle || ''
          });
        }
      }
    } catch (error) {
      // Facebook Pixel is often blocked by ad blockers - this is expected
      // Only log if it's not a blocked resource error
      if (!error.message?.includes('blocked') && !error.message?.includes('ERR_BLOCKED_BY_CLIENT')) {
        console.warn('Quiz Results Cart: Facebook Pixel tracking error:', error);
      }
    }

    // Custom analytics (if available)
    if (typeof window.VelunaAnalytics !== 'undefined' && window.VelunaAnalytics.track) {
      window.VelunaAnalytics.track(eventName, eventData);
    }

    // Console log for debugging
    console.log('Analytics Event:', eventName, eventData);
  }

  /**
   * Initialize add to cart buttons
   */
  function initAddToCartButtons() {
    const addToCartBtns = document.querySelectorAll('[data-results-add-to-cart], [data-mobile-add-to-cart]');

    addToCartBtns.forEach(btn => {
      btn.addEventListener('click', async function(e) {
        e.preventDefault();
        e.stopPropagation();

        const category = this.dataset.category;
        const bundle = this.dataset.bundle;
        const isUpgrade = this.dataset.upgrade === 'true';

        if (!category || !bundle) {
          console.error('Missing category or bundle data');
          return;
        }

        // Disable button and show loading state
        const originalText = this.textContent;
        this.disabled = true;
        this.textContent = 'Adding...';
        this.classList.add('veluna-btn--loading');

        try {
          // Get product handle and variant ID
          const handle = getProductHandle(category, bundle);
          console.log('Quiz Results Cart: Product handle', { category, bundle, handle });
          
          if (!handle) {
            console.error('Quiz Results Cart: Product handle not found for', { category, bundle });
            throw new Error(`Product handle not found for ${category} ${bundle}. Please update PRODUCT_HANDLES mapping.`);
          }

          const variantId = await getProductVariantId(handle);
          console.log('Quiz Results Cart: Product variant ID', { handle, variantId });
          
          if (!variantId) {
            console.error('Quiz Results Cart: Product variant not found for handle', handle);
            throw new Error(`Product variant not found for ${handle}. Please verify product exists in Shopify.`);
          }

          // Add to cart
          const cartItem = await addToCart(variantId, 1, {
            '_quiz_category': category,
            '_quiz_bundle': bundle,
            '_quiz_recommended': !isUpgrade ? 'true' : 'false',
            '_quiz_upgrade': isUpgrade ? 'true' : 'false',
            '_meta_segment': category + '_' + bundle,
            '_meta_intent': isUpgrade ? 'upgrade_click' : 'recommended_add_to_cart',
            '_meta_audience_temperature': window.VelunaAnalytics?.getMetaContext?.({
              category: category,
              bundle: bundle,
              behavior_stage: 'quiz_results_add_to_cart'
            })?.audienceTemperature || '',
            '_meta_audience_stage': window.VelunaAnalytics?.getMetaContext?.({
              category: category,
              bundle: bundle,
              behavior_stage: 'quiz_results_add_to_cart'
            })?.audienceStage || '',
            '_adforge_segment': window.VelunaAnalytics?.getMetaContext?.({
              category: category,
              bundle: bundle,
              behavior_stage: 'quiz_results_add_to_cart'
            })?.adforgeSegment || '',
            '_product_handle': handle
          });

          // Track analytics
          const pricing = window.VelunaQuizResultsOptimal?.VERIFIED_PRICING?.[category]?.[bundle.toLowerCase().replace(/\s+/g, '-')];
          const price = pricing?.discountedPrice || pricing?.price || 0;

          trackEvent('add_to_cart', {
            product_id: variantId,
            product_name: `${category} ${bundle}`,
            category: category,
            bundle: bundle,
            value: price,
            currency: 'USD',
            is_upgrade: isUpgrade,
            product_handle: handle
          });

          // Update cart count
          await updateCartCount();

          // Mark as added to cart (for exit intent)
          sessionStorage.setItem('veluna_cart_added', 'true');

          // Show success notification
          showCartNotification('Added to cart!');

          // Open cart drawer or redirect
          setTimeout(() => {
            showCartDrawer();
          }, 500);

        } catch (error) {
          console.error('Error adding to cart:', error);
          showCartNotification('Error adding to cart. Please try again.', 5000);
          this.disabled = false;
          this.textContent = originalText;
          this.classList.remove('veluna-btn--loading');
        }
      });
    });
  }

  /**
   * Track page view
   */
  function trackPageView(category, bundle, price) {
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant_id');

    trackEvent('view_item', {
      product_id: variantId || 'unknown',
      product_name: `${category} ${bundle}`,
      category: category,
      bundle: bundle,
      value: price,
      currency: 'USD',
      product_handle: getProductHandle(category, bundle)
    });

    // Track quiz results page view
    trackEvent('quiz_results_view', {
      category: category,
      bundle: bundle,
      price: price
    });
  }

  /**
   * Track upgrade click
   */
  function initUpgradeTracking() {
    const upgradeLinks = document.querySelectorAll('[data-scroll-to-upgrade], [data-scroll-to-full-set]');
    
    upgradeLinks.forEach(link => {
      link.addEventListener('click', function() {
        const category = this.closest('[data-quiz-results-optimal]')?.dataset?.category;
        const bundle = 'full-set';

        trackEvent('upgrade_click', {
          category: category,
          from_bundle: 'starter-set', // or 'booster-set'
          to_bundle: bundle
        });
      });
    });
  }

  /**
   * Initialize all functionality
   */
  function init() {
    // Wait for DOM
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }

    console.log('Quiz Results Cart: Initializing...');

    // Wait for quiz results to be rendered
    function waitForResults() {
      const resultsContainer = document.querySelector('[data-quiz-results-container]');
      const hasResults = resultsContainer && resultsContainer.innerHTML.trim() && 
                        !resultsContainer.innerHTML.includes('Loading your personalized results');

      if (hasResults) {
        console.log('Quiz Results Cart: Results loaded, initializing features...');
        
        // Initialize add to cart
        initAddToCartButtons();
        console.log('Quiz Results Cart: Add to cart buttons initialized');

        // Initialize upgrade tracking
        initUpgradeTracking();
        console.log('Quiz Results Cart: Upgrade tracking initialized');

        // Track page view
        const urlParams = new URLSearchParams(window.location.search);
        const category = urlParams.get('category');
        const bundle = urlParams.get('bundle');

        if (category && bundle) {
          const pricing = window.VelunaQuizResultsOptimal?.VERIFIED_PRICING?.[category]?.[bundle.toLowerCase().replace(/\s+/g, '-')];
          const price = pricing?.discountedPrice || pricing?.price || 0;
          trackPageView(category, bundle, price);
          console.log('Quiz Results Cart: Page view tracked', { category, bundle, price });
        } else {
          console.warn('Quiz Results Cart: Missing category or bundle in URL');
        }
      } else {
        // Retry after 100ms
        setTimeout(waitForResults, 100);
      }
    }

    // Start waiting for results
    waitForResults();
  }

  // Initialize
  init();

  // Export for external use
  if (typeof window !== 'undefined') {
    window.VelunaQuizResultsCart = {
      addToCart,
      updateCartCount,
      showCartDrawer,
      showCartNotification,
      trackEvent,
      getProductHandle,
      getProductVariantId,
      PRODUCT_HANDLES // Export for Luna actions
    };
  }
})();

